import React from 'react';
import { motion } from 'framer-motion';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Download, ShoppingCart, Send, FileSpreadsheet, ArrowRight } from 'lucide-react';
export function HowItWorks() {
  const timeline = [{
    icon: <Download className="w-6 h-6" data-id="element-281" />,
    title: 'Install Extension',
    desc: 'Add Cart Tracker to Chrome in seconds. No account required to start.',
    color: 'bg-blue-500'
  }, {
    icon: <ShoppingCart className="w-6 h-6" data-id="element-282" />,
    title: 'Shop & Browse',
    desc: 'Add items to your cart on any website - Amazon, Newegg, Shopify, and more.',
    color: 'bg-[#6366F1]'
  }, {
    icon: <Download className="w-6 h-6" data-id="element-283" />,
    title: 'Capture Cart',
    desc: 'Click the extension icon to automatically extract all item details, prices, SKUs, and quantities.',
    color: 'bg-[#EC4899]'
  }, {
    icon: <Send className="w-6 h-6" data-id="element-284" />,
    title: 'Send or Export',
    desc: 'Send directly to your procurement system or download as Excel to share with your finance team.',
    color: 'bg-purple-500'
  }];
  return <div className="pt-20" data-id="element-285">
      {/* Hero */}
      <Section className="bg-gradient-to-br from-[#EEF2FF] to-white text-center" data-id="element-286">
        <div className="container mx-auto px-4" data-id="element-287">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-288">
            From Shopping Cart to <br data-id="element-289" />
            <span className="text-[#6366F1]" data-id="element-290">Finance Approval</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10" data-id="element-291">
            See how Cart Tracker captures purchase information and delivers it
            where you need it.
          </p>
        </div>
      </Section>

      {/* Timeline */}
      <Section className="bg-white" data-id="element-292">
        <div className="container mx-auto px-4 md:px-6" data-id="element-293">
          <div className="relative" data-id="element-294">
            {/* Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 transform md:-translate-x-1/2" data-id="element-295"></div>

            {timeline.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.2
          }} className={`relative flex items-center gap-8 mb-16 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-296">
                {/* Icon Marker */}
                <div className={`absolute left-8 md:left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full border-4 border-white shadow-lg flex items-center justify-center z-10 ${item.color} text-white`} data-id="element-297">
                  {item.icon}
                </div>

                {/* Content */}
                <div className={`ml-20 md:ml-0 w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16 md:text-left'}`} data-id="element-298">
                  <Card className="p-6" data-id="element-299">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-300">
                      {item.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-301">{item.desc}</p>
                  </Card>
                </div>

                {/* Empty space for the other side */}
                <div className="hidden md:block w-1/2" data-id="element-302"></div>
              </motion.div>)}
          </div>
        </div>
      </Section>

      {/* Export Options */}
      <Section className="bg-gradient-to-br from-[#EEF2FF] to-white" data-id="element-303">
        <div className="container mx-auto px-4 text-center" data-id="element-304">
          <h2 className="text-3xl font-bold text-gray-900 mb-12" data-id="element-305">
            Flexible Export Options
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-306">
            <Card className="p-8" data-id="element-307">
              <div className="flex items-center justify-between mb-6" data-id="element-308">
                <div className="font-bold text-xl" data-id="element-309">Cart Data</div>
                <ArrowRight className="text-gray-400" data-id="element-310" />
                <div className="font-bold text-xl text-[#6366F1]" data-id="element-311">
                  Procurement System
                </div>
              </div>
              <p className="text-gray-600 text-sm" data-id="element-312">
                Send cart information directly to your procurement platform via
                API integration or webhook.
              </p>
            </Card>

            <Card className="p-8" data-id="element-313">
              <div className="flex items-center justify-between mb-6" data-id="element-314">
                <div className="font-bold text-xl" data-id="element-315">Cart Data</div>
                <ArrowRight className="text-gray-400" data-id="element-316" />
                <div className="font-bold text-xl text-[#EC4899]" data-id="element-317">
                  Excel Download
                </div>
              </div>
              <p className="text-gray-600 text-sm" data-id="element-318">
                Download a formatted Excel spreadsheet with all item details
                ready to share with your finance team.
              </p>
            </Card>
          </div>

          <div className="mt-12" data-id="element-319">
            <Button variant="primary" className="px-8" onClick={() => window.open('https://calendly.com/alex-thruline/cart-tracker', '_blank')} data-id="element-320">
              Schedule a Demo
            </Button>
          </div>
        </div>
      </Section>
    </div>;
}